import { cn } from "@/lib/utils";
import { Loader2, RotateCcw, Save } from "lucide-react";
import type { ReactNode } from "react";

interface AdminSaveBarProps {
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  rightSlot?: ReactNode;
  showKbdHint?: boolean;
}

export function AdminSaveBar({
  isDirty,
  isSaving,
  onSave,
  onDiscard,
  rightSlot,
  showKbdHint = true,
}: AdminSaveBarProps) {
  return (
    <div className="sticky bottom-4 z-20 -mx-4 -mb-4 mt-6 sm:-mx-6 sm:-mb-6 lg:-mx-8 lg:-mb-8">
      <div className="mx-4 sm:mx-6 lg:mx-8">
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 backdrop-blur-xl transition-all duration-300 sm:px-5",
            isDirty
              ? "border-white/10 bg-zinc-900/90 shadow-2xl shadow-black/60"
              : "border-white/[0.04] bg-white/[0.02] shadow-none",
          )}
        >
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving || !isDirty}
              className={cn(
                "relative flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-150 active:scale-95",
                isDirty
                  ? "bg-white text-black shadow-lg shadow-white/10 hover:bg-white/90"
                  : "cursor-not-allowed bg-white/[0.06] text-white/20",
              )}
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Guardar
              {isDirty && !isSaving && (
                <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5" aria-hidden>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70 opacity-60" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white/80" />
                </span>
              )}
            </button>

            {isDirty && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("¿Descartar los cambios sin guardar?")) {
                    onDiscard();
                  }
                }}
                disabled={isSaving}
                className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-2 text-sm font-medium text-white/40 transition-all hover:bg-white/[0.07] hover:text-white/70 active:scale-95 disabled:pointer-events-none"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Descartar
              </button>
            )}

            {showKbdHint && isDirty && (
              <span className="hidden items-center gap-1 text-xs text-white/20 sm:flex">
                <kbd className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-1.5 py-0.5 text-[10px]">⌘</kbd>
                <span>+</span>
                <kbd className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-1.5 py-0.5 text-[10px]">S</kbd>
              </span>
            )}
          </div>

          {rightSlot && (
            <div className="flex flex-wrap items-center gap-2">{rightSlot}</div>
          )}
        </div>
      </div>
    </div>
  );
}
